import { useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import {
  ArrowDownAZIcon,
  ArrowUpZAIcon,
  BanIcon,
  CheckIcon,
  ClipboardListIcon,
  FilterIcon,
  GlobeIcon,
  InboxIcon,
  LayoutGridIcon,
  ListIcon,
  MapIcon,
  MapPinIcon,
  PencilIcon,
  PhoneIcon,
  PlusIcon,
  SearchIcon,
  XIcon,
} from 'lucide-react';
import {
  CANONICAL_SPECIALISATIONS as SPECIALISATIONS,
  CARE_LEVEL_LABELS,
  PROVIDER_SEARCH_CEILING,
  specialisationLabel,
  type CareLevel,
  type Provider,
} from '@kennisbank/shared';
import { ApiError, apiGet, apiPatch, apiPost } from '../api/client.js';
import { useAuth } from '../auth/AuthProvider.js';
import { Layout } from '../components/Layout.js';
import { WijzigingsverzoekLink } from '../components/WijzigingsverzoekButton.js';
import {
  EMPTY_PROVIDER_FORM,
  ProviderFormDialog,
  formToPayload,
  providerToForm,
} from '../components/ProviderFormDialog.js';
import { CareLevelPills, ProviderMap } from '../components/ProviderMap.js';
import { IntakeControl } from '../components/IntakeControl.js';

/**
 * Practitioner Sociale Kaart (slice 2a, design-aligned).
 *
 * Layout: filter rail (provincie radio / type radio / doelgroep chips /
 * specialisatie chips) + toolbar (search + count + Lijst|Kaart toggle) +
 * card grid. Empty + many-results states match the magicpatterns mock.
 *
 * Map view (react-leaflet) renders one pin per provider with coordinates.
 * Empty + many-results states match the magicpatterns mock.
 */

interface SearchResult {
  items: Provider[];
  nextCursor?: string;
  total: number;
  grandTotal: number;
}

// Alle 12 NL-provincies sinds de LVVP-ingest landelijk dekt (was vóór die
// ingest beperkt tot de 3 geseede provincies). 'Landelijk' blijft voor
// e-health/online-aanbieders zonder fysieke provincie.
const PROVINCES = [
  'Alle',
  'Drenthe',
  'Flevoland',
  'Friesland',
  'Gelderland',
  'Groningen',
  'Limburg',
  'Noord-Brabant',
  'Noord-Holland',
  'Overijssel',
  'Utrecht',
  'Zeeland',
  'Zuid-Holland',
  'Landelijk',
] as const;
type Province = (typeof PROVINCES)[number];

const TYPES = [
  { value: '', label: 'Alle' },
  { value: 'ggz_instelling', label: 'GGZ-instelling' },
  { value: 'vrijgevestigd', label: 'Vrijgevestigd' },
  { value: 'groepspraktijk', label: 'Groepspraktijk' },
  { value: 'e_health', label: 'E-health' },
] as const;

const AUDIENCES = [
  { value: 'volwassenen', label: 'Volwassenen' },
  { value: 'jeugd', label: 'Jeugd' },
  { value: 'jongeren', label: 'Jongeren' },
  { value: 'ouderen', label: 'Ouderen' },
  { value: 'paren', label: 'Paren' },
  { value: 'gezinnen', label: 'Gezinnen' },
  { value: 'vrouwen', label: 'Vrouwen' },
] as const;

const CARE_LEVELS = Object.entries(CARE_LEVEL_LABELS) as [CareLevel, string][];

// Op weergavenaam sorteren, niet op opgeslagen waarde: anders staat
// "Stemming & depressie" tussen de D's.
const SPECIALISATIONS_SORTED = [...SPECIALISATIONS].sort((a, b) =>
  specialisationLabel(a).localeCompare(specialisationLabel(b), 'nl'),
);
// 61 chips ineens maakte de filterrail een muur; de rest zit achter "Toon alle".
const SPECIALISATIONS_COLLAPSED = 12;

const nl = new Intl.NumberFormat('nl-NL');

// Talen die LVVP-aanbieders naast Nederlands kunnen bieden. Curated subset van
// de LVVP-taallijst - de meest gevraagde; uitbreiden als de data erom vraagt.
// Waarden moeten exact matchen met wat de crawler opslaat (lvvp/parse.py).
const LANGUAGES = [
  'Engels',
  'Duits',
  'Frans',
  'Spaans',
  'Turks',
  'Arabisch',
  'Pools',
  'Russisch',
] as const;

// Bron-filter: alleen de twee waarden die in de praktijk voorkomen. SUPPOHRT
// zelf toegevoegd (suppohrt_manual) vs. via de geautomatiseerde LVVP-ingest.
const SOURCES = [
  { value: '', label: 'Alle' },
  { value: 'suppohrt_manual', label: 'SUPPOHRT' },
  { value: 'lvvp', label: 'Via LVVP' },
] as const;

const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

export function SocialeKaartPage(): JSX.Element {
  const { user } = useAuth();
  const isAdmin = user?.role === 'suppohrt_admin';
  // Demo-omgeving (issue #44): uitsluitend de clusterkaart — grid/lijst,
  // praktijkdetails en toevoegen/voorstellen bestaan niet in de demo.
  const demo = user?.demo === true;

  // Deep-link support: ?providerId=<id> opens the page focused on that
  // single provider (used by the Kennisagent's find_zorgaanbieders tool
  // citations). When set, normal filters/search are suppressed and we
  // fetch the one provider directly via GET /api/v1/providers/:id.
  // Ook in demo: de doorklik vanuit Mila's antwoord moet de fictieve
  // praktijk tonen (besluit Jos 10 aug 2026, herziening van issue #44).
  const [searchParams, setSearchParams] = useSearchParams();
  const focusedProviderId = searchParams.get('providerId');

  const [province, setProvince] = useState<Province>('Alle');
  const [type, setType] = useState<string>('');
  const [source, setSource] = useState<string>('');
  const [audiences, setAudiences] = useState<string[]>([]);
  const [specialisations, setSpecialisations] = useState<string[]>([]);
  const [languages, setLanguages] = useState<string[]>([]);
  const [careLevels, setCareLevels] = useState<CareLevel[]>([]);
  const [showAllSpecs, setShowAllSpecs] = useState(false);
  const [letter, setLetter] = useState<string>('');
  const [sort, setSort] = useState<'asc' | 'desc'>('asc');
  const [pageSize, setPageSize] = useState(25);
  const [q, setQ] = useState('');
  // Pas 250 ms na de laatste toetsaanslag zoeken; anders vuurt elke letter een
  // verzoek en flikkeren teller en resultaten tijdens het typen.
  const [qDebounced, setQDebounced] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setQDebounced(q.trim()), 250);
    return () => clearTimeout(t);
  }, [q]);
  const [view, setView] = useState<'grid' | 'list' | 'map'>(demo ? 'map' : 'grid');
  const [filtersOpen, setFiltersOpen] = useState(false);

  // Single-provider focus state — populated only when ?providerId= is in
  // the URL. Separate from `items` so clearing the focus doesn't require
  // a re-fetch of the full list.
  const [focusedProvider, setFocusedProvider] = useState<Provider | null>(null);
  const [focusedError, setFocusedError] = useState<string | null>(null);
  const clearProviderFocus = (): void => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('providerId');
      return next;
    });
    setFocusedProvider(null);
    setFocusedError(null);
  };

  useEffect(() => {
    if (!focusedProviderId) {
      setFocusedProvider(null);
      setFocusedError(null);
      return;
    }
    let cancelled = false;
    apiGet<Provider>(`/api/v1/providers/${encodeURIComponent(focusedProviderId)}`)
      .then((p) => {
        if (!cancelled) setFocusedProvider(p);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setFocusedProvider(null);
        setFocusedError(
          err instanceof ApiError && err.status === 404
            ? 'Deze zorgaanbieder is niet gevonden. Mogelijk verwijderd.'
            : err instanceof ApiError
              ? err.message
              : 'Kan de zorgaanbieder nu niet laden.',
        );
      });
    return () => {
      cancelled = true;
    };
  }, [focusedProviderId]);

  // Admin click-to-edit on a card. Only mounted when isAdmin === true so
  // a non-admin can never trigger it client-side. Backend re-checks role
  // on PATCH per A1 (defense in depth).
  const [editing, setEditing] = useState<Provider | null>(null);
  const [reload, setReload] = useState(0);

  // Praktijk toevoegen/voorstellen — open voor iedereen. Admin maakt direct aan;
  // een POH dient een voorstel in dat als 'pending' op admin-goedkeuring wacht.
  const [adding, setAdding] = useState(false);

  // Aanvragen-tab (alleen admin): de wachtrij met voorgestelde praktijken.
  const [tab, setTab] = useState<'kaart' | 'aanvragen'>('kaart');
  const [pending, setPending] = useState<Provider[]>([]);
  useEffect(() => {
    if (!isAdmin) return;
    let cancelled = false;
    apiGet<SearchResult>('/api/v1/providers', {
      status: 'pending',
      limit: PROVIDER_SEARCH_CEILING,
    })
      .then((res) => {
        if (!cancelled) setPending(res.items);
      })
      .catch(() => {
        if (!cancelled) setPending([]);
      });
    return () => {
      cancelled = true;
    };
  }, [isAdmin, reload]);

  const [items, setItems] = useState<Provider[]>([]);
  const [total, setTotal] = useState(0);
  // Aantal zonder filters, zodat de teller "25 van 2.896" kan zeggen.
  const [grandTotal, setGrandTotal] = useState<number | null>(null);
  const [cursor, setCursor] = useState<string | undefined>();
  const [nextCursor, setNextCursor] = useState<string | undefined>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // The map shows pins for the whole filtered set, not one page — ask for
  // everything in one call (the backend's full-scan ceiling). Grid/list keep
  // the user-chosen page size.
  const effectiveLimit = view === 'map' ? PROVIDER_SEARCH_CEILING : pageSize;

  const filterKey = useMemo(
    () =>
      JSON.stringify({
        province,
        type,
        source,
        audiences,
        specialisations,
        languages,
        careLevels,
        letter,
        sort,
        effectiveLimit,
        q: qDebounced,
      }),
    [
      province,
      type,
      source,
      audiences,
      specialisations,
      languages,
      careLevels,
      letter,
      sort,
      effectiveLimit,
      qDebounced,
    ],
  );
  useEffect(() => setCursor(undefined), [filterKey]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    apiGet<SearchResult>('/api/v1/providers', {
      province: province === 'Alle' ? undefined : province,
      type: type || undefined,
      source: source || undefined,
      targetAudience: audiences.length > 0 ? audiences : undefined,
      specialisation: specialisations.length > 0 ? specialisations : undefined,
      language: languages.length > 0 ? languages : undefined,
      careLevel: careLevels.length > 0 ? careLevels : undefined,
      letter: letter || undefined,
      sort,
      q: qDebounced || undefined,
      limit: effectiveLimit,
      cursor,
    })
      .then((res) => {
        if (cancelled) return;
        setItems((prev) => (cursor ? [...prev, ...res.items] : res.items));
        setNextCursor(res.nextCursor);
        setTotal(res.total);
        setGrandTotal(res.grandTotal);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof ApiError ? err.message : 'Kan de Sociale Kaart nu niet laden.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [
    filterKey,
    cursor,
    province,
    type,
    source,
    audiences,
    specialisations,
    languages,
    careLevels,
    letter,
    sort,
    effectiveLimit,
    qDebounced,
    reload,
  ]);

  // Chip uit de gedebouncete zoekterm: die hoort bij het getoonde aantal. De
  // ruwe `q` loopt 250 ms voor en liet de teller een oud aantal "bevestigen".
  const trimmedQ = qDebounced.trim();
  const filterChips: { key: string; group: string; label: string; onRemove: () => void }[] = [
    ...(trimmedQ
      ? [{ key: 'q', group: 'Zoekterm', label: `"${trimmedQ}"`, onRemove: () => setQ('') }]
      : []),
    ...(province !== 'Alle'
      ? [
          {
            key: 'province',
            group: 'Provincie',
            label: province,
            onRemove: () => setProvince('Alle'),
          },
        ]
      : []),
    ...(type
      ? [
          {
            key: 'type',
            group: 'Type',
            label: typeLabel(type as Provider['type']),
            onRemove: () => setType(''),
          },
        ]
      : []),
    ...(source
      ? [
          {
            key: 'source',
            group: 'Toegevoegd via',
            label: SOURCES.find((x) => x.value === source)?.label ?? source,
            onRemove: () => setSource(''),
          },
        ]
      : []),
    ...audiences.map((a) => ({
      key: `aud-${a}`,
      group: 'Doelgroep',
      label: audienceLabel(a),
      onRemove: () => toggleAudience(a),
    })),
    ...careLevels.map((c) => ({
      key: `care-${c}`,
      group: 'Zorgniveau',
      label: CARE_LEVEL_LABELS[c],
      onRemove: () => toggleCareLevel(c),
    })),
    ...specialisations.map((s) => ({
      key: `spec-${s}`,
      group: 'Specialisatie',
      label: specialisationLabel(s),
      onRemove: () => toggleSpecialisation(s),
    })),
    ...languages.map((l) => ({
      key: `lang-${l}`,
      group: 'Taal',
      label: l,
      onRemove: () => toggleLanguage(l),
    })),
    ...(letter
      ? [{ key: 'letter', group: 'Beginletter', label: letter, onRemove: () => setLetter('') }]
      : []),
  ];
  const activeFilters = filterChips.length;

  function toggleSpecialisation(s: string): void {
    setSpecialisations((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]));
  }

  function toggleAudience(a: string): void {
    setAudiences((prev) => (prev.includes(a) ? prev.filter((x) => x !== a) : [...prev, a]));
  }

  function toggleLanguage(l: string): void {
    setLanguages((prev) => (prev.includes(l) ? prev.filter((x) => x !== l) : [...prev, l]));
  }

  function toggleCareLevel(c: CareLevel): void {
    setCareLevels((prev) => (prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c]));
  }

  function resetFilters(): void {
    setProvince('Alle');
    setType('');
    setSource('');
    setAudiences([]);
    setSpecialisations([]);
    setLanguages([]);
    setCareLevels([]);
    setLetter('');
    setQ('');
  }

  return (
    <Layout>
      {/* overflow-x-hidden: één te breed element mag nooit de hele pagina laten scrollen. */}
      <div className="flex h-full overflow-x-hidden">
        {/* Mobile drawer scrim */}
        {filtersOpen && (
          <button
            type="button"
            aria-label="Sluit filters"
            onClick={() => setFiltersOpen(false)}
            className="fixed inset-0 z-30 bg-ink-900/40 lg:hidden"
          />
        )}
        {/* Filter rail — fixed drawer on mobile, static column on lg+. */}
        <aside
          className={`thin-scroll fixed inset-y-0 left-0 z-40 flex w-72 shrink-0 flex-col overflow-y-auto border-r border-brand-100 bg-white transition-transform duration-200 lg:static lg:translate-x-0 ${
            filtersOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
          }`}
        >
          <div className="flex items-center justify-between border-b border-brand-50 p-5">
            <div className="flex items-center gap-2">
              <FilterIcon size={15} className="text-brand-600" />
              <h2 className="text-sm font-semibold text-ink-900">Filters</h2>
            </div>
            <div className="flex items-center gap-2">
              {activeFilters > 0 && (
                <button
                  type="button"
                  onClick={resetFilters}
                  className="text-[11px] font-medium text-brand-600 hover:text-ink-900"
                >
                  Wis ({activeFilters})
                </button>
              )}
              {/* Mobile-only close — desktop has no need. */}
              <button
                type="button"
                onClick={() => setFiltersOpen(false)}
                aria-label="Sluit filters"
                className="rounded-md p-1 text-ink-900/60 hover:bg-brand-50 hover:text-ink-900 lg:hidden"
              >
                <XIcon size={16} />
              </button>
            </div>
          </div>

          <div className="space-y-6 p-5">
            <FilterGroup label="Provincie">
              <div className="space-y-1">
                {PROVINCES.map((p) => (
                  <RadioRow
                    key={p}
                    label={p}
                    checked={province === p}
                    onChange={() => setProvince(p)}
                  />
                ))}
              </div>
            </FilterGroup>

            <FilterGroup label="Type zorgverlener">
              <div className="space-y-1">
                {TYPES.map((t) => (
                  <RadioRow
                    key={t.value || 'all'}
                    label={t.label}
                    checked={type === t.value}
                    onChange={() => setType(t.value)}
                  />
                ))}
              </div>
            </FilterGroup>

            {/* Herkomst (bijv. "Via LVVP") is provenance-info die alleen admins
                mogen zien - een POH hoeft niet te weten waar de lijst vandaan
                komt. Filter dus alleen tonen voor suppohrt_admin. */}
            {isAdmin && (
              <FilterGroup label="Toegevoegd via">
                <div className="space-y-1">
                  {SOURCES.map((s) => (
                    <RadioRow
                      key={s.value || 'all'}
                      label={s.label}
                      checked={source === s.value}
                      onChange={() => setSource(s.value)}
                    />
                  ))}
                </div>
              </FilterGroup>
            )}

            <FilterGroup label="Doelgroep">
              <div className="flex flex-wrap gap-1.5">
                {AUDIENCES.map((a) => (
                  <Chip
                    key={a.value}
                    label={a.label}
                    active={audiences.includes(a.value)}
                    onClick={() => toggleAudience(a.value)}
                  />
                ))}
              </div>
            </FilterGroup>

            <FilterGroup label="Zorgniveau">
              <div className="flex flex-wrap gap-1.5">
                {CARE_LEVELS.map(([value, label]) => (
                  <Chip
                    key={value}
                    label={label}
                    active={careLevels.includes(value)}
                    onClick={() => toggleCareLevel(value)}
                  />
                ))}
              </div>
            </FilterGroup>

            <FilterGroup label="Specialisatie">
              <div className="flex flex-wrap gap-1.5">
                {SPECIALISATIONS_SORTED.filter(
                  (s, i) =>
                    showAllSpecs || i < SPECIALISATIONS_COLLAPSED || specialisations.includes(s),
                ).map((s) => (
                  <Chip
                    key={s}
                    label={specialisationLabel(s)}
                    active={specialisations.includes(s)}
                    onClick={() => toggleSpecialisation(s)}
                  />
                ))}
              </div>
              <button
                type="button"
                aria-expanded={showAllSpecs}
                onClick={() => setShowAllSpecs((v) => !v)}
                className="mt-2 text-[12px] font-medium text-brand-700 hover:text-ink-900"
              >
                {showAllSpecs ? 'Toon minder' : `Toon alle ${SPECIALISATIONS_SORTED.length}`}
              </button>
            </FilterGroup>

            <FilterGroup label="Taal">
              <div className="flex flex-wrap gap-1.5">
                {LANGUAGES.map((l) => (
                  <Chip
                    key={l}
                    label={l}
                    active={languages.includes(l)}
                    onClick={() => toggleLanguage(l)}
                  />
                ))}
              </div>
            </FilterGroup>
          </div>
        </aside>

        {/* Main */}
        <div className="flex min-w-0 flex-1 flex-col">
          <div className="flex shrink-0 flex-col gap-2 border-b border-brand-100 bg-white px-4 py-3 sm:flex-row sm:items-center sm:gap-3 sm:px-6">
            {/* Row 1 on mobile (single row on sm+): filter trigger + search. */}
            <div className="flex items-center gap-2 sm:contents">
              {/* Mobile-only filter trigger. Hidden on lg+ where the rail is permanent. */}
              <button
                type="button"
                onClick={() => setFiltersOpen(true)}
                className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-brand-200 px-2.5 py-1.5 text-xs font-medium text-ink-800 hover:bg-brand-50 lg:hidden"
              >
                <FilterIcon size={14} /> Filters
                {activeFilters > 0 && (
                  <span className="ml-0.5 inline-flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-brand-600 px-1 text-[10px] font-semibold text-white">
                    {activeFilters}
                  </span>
                )}
              </button>
              <div className="relative max-w-xl flex-1">
                <SearchIcon
                  size={15}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-900/40"
                />
                <input
                  value={q}
                  onChange={(e) => {
                    setQ(e.target.value);
                    // Zoeken vanuit de Aanvragen-tab liet de teller wel lopen
                    // maar het paneel op Aanvragen staan.
                    setTab('kaart');
                  }}
                  placeholder="Zoek op naam, plaats of specialisatie…"
                  className="w-full rounded-lg border border-brand-100 bg-brand-50/40 py-2 pl-9 pr-3 text-sm placeholder:text-ink-900/40 focus:border-brand-300 focus:bg-white focus:outline-none focus:ring-2 focus:ring-brand-200/40"
                />
              </div>
            </div>
            {/* Row 2 on mobile (inline tail on sm+): count + sort + view toggle. */}
            <div className="flex items-center justify-end gap-3 sm:ml-auto sm:w-auto">
              <div className="flex items-center gap-2">
                {/* Demo: alle schrijfroutes zitten dicht — knop weglaten. */}
                {!demo && (
                  <button
                    type="button"
                    onClick={() => setAdding(true)}
                    className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-md bg-brand-600 px-2.5 py-1.5 text-xs font-medium text-white shadow-sm transition-colors hover:bg-brand-700"
                  >
                    <PlusIcon size={14} />
                    <span className="hidden sm:inline">
                      {isAdmin ? 'Praktijk toevoegen' : 'Praktijk voorstellen'}
                    </span>
                  </button>
                )}
                {isAdmin && (
                  <button
                    type="button"
                    onClick={() => setTab((t) => (t === 'aanvragen' ? 'kaart' : 'aanvragen'))}
                    className={`inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-md border px-2.5 py-1.5 text-xs font-medium transition-colors ${
                      tab === 'aanvragen'
                        ? 'border-brand-600 bg-brand-50 text-brand-700'
                        : 'border-brand-200 text-ink-800 hover:bg-brand-50'
                    }`}
                    title="Openstaande praktijkvoorstellen beoordelen"
                  >
                    <ClipboardListIcon size={14} />
                    <span className="hidden sm:inline">Aanvragen</span>
                    {pending.length > 0 && (
                      <span className="inline-flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-brand-600 px-1 text-[10px] font-semibold text-white">
                        {pending.length}
                      </span>
                    )}
                  </button>
                )}
                {/* Aantal per pagina. Niet zinvol op de kaart (alle pins ineens). */}
                {tab === 'kaart' && view !== 'map' && (
                  <label className="flex items-center gap-1.5 text-xs text-ink-900/60">
                    <span className="hidden sm:inline">Toon</span>
                    <select
                      value={pageSize}
                      onChange={(e) => setPageSize(Number(e.target.value))}
                      className="rounded-md border border-brand-200 bg-white py-1.5 pl-2 pr-6 text-xs font-medium text-ink-800 hover:bg-brand-50 focus:border-brand-300 focus:outline-none focus:ring-2 focus:ring-brand-200/40"
                    >
                      {/* De full-scan ceiling van de backend = "Alle". */}
                      {[
                        { value: 25, label: '25' },
                        { value: 50, label: '50' },
                        { value: 100, label: '100' },
                        { value: PROVIDER_SEARCH_CEILING, label: 'Alle' },
                      ].map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </label>
                )}
                {/* Kaart-weergave heeft geen volgorde, dus sorteerknop weglaten. */}
                {tab === 'kaart' && view !== 'map' && (
                  <button
                    type="button"
                    onClick={() => setSort((s) => (s === 'asc' ? 'desc' : 'asc'))}
                    className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-brand-200 px-2.5 py-1.5 text-xs font-medium text-ink-800 hover:bg-brand-50"
                    title={sort === 'asc' ? 'Sorteer Z-A' : 'Sorteer A-Z'}
                  >
                    {sort === 'asc' ? <ArrowDownAZIcon size={14} /> : <ArrowUpZAIcon size={14} />}
                    {sort === 'asc' ? 'A-Z' : 'Z-A'}
                  </button>
                )}
                {/* Demo: de kaart is de enige weergave — geen raster/lijst. */}
                {tab === 'kaart' && !demo && (
                  <div className="flex items-center gap-0.5 rounded-lg bg-brand-50 p-0.5">
                    <ViewToggle
                      active={view === 'grid'}
                      onClick={() => setView('grid')}
                      icon={<LayoutGridIcon size={14} />}
                      label="Raster"
                    />
                    <ViewToggle
                      active={view === 'list'}
                      onClick={() => setView('list')}
                      icon={<ListIcon size={14} />}
                      label="Lijst"
                    />
                    <ViewToggle
                      active={view === 'map'}
                      onClick={() => setView('map')}
                      icon={<MapIcon size={14} />}
                      label="Kaart"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* A-Z letterbalk — filtert op beginletter van de naam. Niet zinvol in
              kaartweergave of bij een direct-geopende aanbieder. Mobiel: één
              veegbare rij (3 rijen letters blokkeerden het halve scherm);
              vanaf sm: het gewone wrappende rooster. */}
          {tab === 'kaart' && view !== 'map' && !focusedProviderId && (
            <div className="thin-scroll flex shrink-0 items-center gap-1 overflow-x-auto border-b border-brand-100 bg-white px-4 py-2 sm:flex-wrap sm:overflow-x-visible sm:px-6">
              <LetterButton label="Alle" active={letter === ''} onClick={() => setLetter('')} />
              {ALPHABET.map((l) => (
                <LetterButton
                  key={l}
                  label={l}
                  active={letter === l}
                  onClick={() => setLetter(letter === l ? '' : l)}
                />
              ))}
              {/* Cijfers/overige — namen die niet met A-Z beginnen (bv. "113", "1nP"). */}
              <LetterButton
                label="#"
                active={letter === '#'}
                onClick={() => setLetter(letter === '#' ? '' : '#')}
              />
            </div>
          )}

          {tab === 'kaart' && !focusedProviderId && (
            <ResultBar
              total={total}
              grandTotal={grandTotal}
              loading={loading}
              chips={filterChips}
              onReset={resetFilters}
            />
          )}

          <div className="min-h-0 flex-1 overflow-hidden">
            <div className="thin-scroll h-full overflow-y-auto">
              <div className="space-y-4 p-6">
                {tab === 'aanvragen' ? (
                  <PendingReview
                    providers={pending}
                    onDecision={() => setReload((n) => n + 1)}
                    onClose={() => setTab('kaart')}
                  />
                ) : (
                  <>
                    {focusedProviderId && (
                      // Direct-link banner — sits above either the focused
                      // provider card OR an error state. "Toon alle aanbieders"
                      // strips the query param and returns to the normal list.
                      <div className="flex items-center justify-between gap-3 rounded-lg border border-brand-200 bg-brand-50/60 px-4 py-2 text-sm">
                        <span className="text-ink-900/70">
                          Direct geopend vanuit de Kennisagent.
                        </span>
                        <button
                          type="button"
                          onClick={clearProviderFocus}
                          className="rounded-md border border-brand-200 bg-white px-3 py-1 text-xs font-medium text-ink-800 hover:bg-brand-50"
                        >
                          Toon alle aanbieders
                        </button>
                      </div>
                    )}

                    {focusedError && (
                      <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
                        {focusedError}
                      </div>
                    )}

                    {!focusedProviderId && error && (
                      <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
                        {error}
                      </div>
                    )}

                    {!focusedProviderId && !loading && items.length === 0 && !error && (
                      <ZeroResults onReset={resetFilters} hasFilters={activeFilters > 0} />
                    )}

                    {(() => {
                      const displayProviders = focusedProviderId
                        ? focusedProvider
                          ? [focusedProvider]
                          : []
                        : items;
                      // Focus toont altijd de praktijkkaart — op de kaartweergave
                      // (en zeker de vergrendelde democlusterkaart) is één losse
                      // marker niet te vinden.
                      if (view === 'map' && !focusedProviderId) {
                        return (
                          <ProviderMap providers={displayProviders} isAdmin={isAdmin} demo={demo} />
                        );
                      }
                      if (view === 'list') {
                        return (
                          <div className="divide-y divide-brand-50 overflow-hidden rounded-xl border border-brand-100 bg-white">
                            {displayProviders.map((p) => (
                              <ProviderRow
                                key={p.id}
                                provider={p}
                                onEdit={isAdmin ? () => setEditing(p) : undefined}
                                showSource={isAdmin}
                              />
                            ))}
                          </div>
                        );
                      }
                      return (
                        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                          {displayProviders.map((p) => (
                            <ProviderCard
                              key={p.id}
                              provider={p}
                              onEdit={isAdmin ? () => setEditing(p) : undefined}
                              showSource={isAdmin}
                            />
                          ))}
                        </div>
                      );
                    })()}

                    {view !== 'map' && !focusedProviderId && nextCursor && (
                      <div className="flex flex-col items-center gap-2 pt-2">
                        <span className="text-xs text-ink-900/50">
                          {nl.format(items.length)} van {nl.format(total)} getoond
                        </span>
                        <button
                          type="button"
                          onClick={() => setCursor(nextCursor)}
                          disabled={loading}
                          className="rounded-lg border border-brand-200 bg-white px-4 py-2 text-sm font-medium text-ink-800 transition-colors hover:bg-brand-50 disabled:opacity-60"
                        >
                          {loading ? 'Laden…' : 'Toon meer'}
                        </button>
                      </div>
                    )}

                    <p className="pt-4 text-center text-xs text-ink-900/40">
                      Iets niet juist?{' '}
                      <a
                        className="underline hover:text-ink-900/60"
                        href="mailto:kennisportaal@suppohrt.nl"
                      >
                        Mail kennisportaal@suppohrt.nl
                      </a>
                    </p>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {adding && (
        <ProviderFormDialog
          title={isAdmin ? 'Nieuwe praktijk' : 'Praktijk voorstellen'}
          initial={EMPTY_PROVIDER_FORM}
          mode="create"
          variant={isAdmin ? 'admin' : 'suggestion'}
          onClose={() => setAdding(false)}
          onSubmit={async (form) => {
            const payload = formToPayload(form) as Record<string, unknown>;
            if (isAdmin) {
              await apiPost('/api/v1/providers', payload);
            } else {
              // status + source dwingt de server af bij een voorstel; niet meesturen.
              const { status: _s, source: _src, ...rest } = payload;
              void _s;
              void _src;
              await apiPost('/api/v1/providers/suggestions', rest);
            }
            setAdding(false);
            setReload((n) => n + 1);
          }}
        />
      )}

      {editing && isAdmin && (
        <ProviderFormDialog
          title={`Bewerk: ${editing.name}`}
          initial={providerToForm(editing)}
          mode="edit"
          onClose={() => setEditing(null)}
          onSubmit={async (form, opts) => {
            // Strip `source` from the PATCH: the update-route schema doesn't
            // accept 'lvvp', and source never changes on edit anyway - the
            // backend preserves the existing value (and reads it for the
            // LVVP auto-lock) via the merge in ProviderService.update.
            const {
              lastVerifiedAt: _omit,
              source: _omitSource,
              ...patch
            } = formToPayload(form) as Record<string, unknown> & { lastVerifiedAt: string };
            void _omit;
            void _omitSource;
            await apiPatch(
              `/api/v1/providers/${editing.id}`,
              patch,
              opts.bumpVerified ? undefined : { bumpVerified: 'false' },
            );
            setEditing(null);
            setReload((n) => n + 1);
          }}
        />
      )}
    </Layout>
  );
}

/**
 * Teller + "Je zoekt op". Staat direct boven de resultaten zodat zichtbaar is
 * dat wat je typt of aanklikt de lijst vernauwt: het aantal licht kort op als
 * het verandert en elk actief filter is los weg te klikken.
 */
function ResultBar({
  total,
  grandTotal,
  loading,
  chips,
  onReset,
}: {
  total: number;
  grandTotal: number | null;
  loading: boolean;
  chips: { key: string; group: string; label: string; onRemove: () => void }[];
  onReset: () => void;
}): JSX.Element {
  const reduceMotion = useReducedMotion();
  const narrowed = chips.length > 0;
  const noun = total === 1 ? 'zorgverlener' : 'zorgverleners';
  return (
    <div
      className={`flex shrink-0 flex-wrap items-center gap-x-3 gap-y-2 border-b px-4 py-2.5 transition-colors sm:px-6 ${
        narrowed ? 'border-brand-200 bg-brand-50/70' : 'border-brand-100 bg-white'
      }`}
    >
      <div
        aria-live="polite"
        className={`text-sm ${narrowed ? 'text-ink-900' : 'text-ink-900/60'} ${loading ? 'opacity-60' : ''}`}
      >
        {narrowed ? (
          <>
            {/* key={total}: elke nieuwe uitkomst hermount het getal en speelt de
                oplichting opnieuw af. */}
            <motion.strong
              key={total}
              initial={
                reduceMotion ? false : { backgroundColor: 'rgba(38,145,145,0.35)', scale: 1.15 }
              }
              animate={{ backgroundColor: 'rgba(38,145,145,0)', scale: 1 }}
              transition={{ duration: 0.6, ease: 'easeOut' }}
              className="inline-block rounded px-1 text-base font-bold text-brand-700"
            >
              {nl.format(total)}
            </motion.strong>
            {grandTotal !== null ? (
              <>
                {' '}
                van {nl.format(grandTotal)} {grandTotal === 1 ? 'zorgverlener' : 'zorgverleners'}
              </>
            ) : (
              <> {noun} gevonden</>
            )}
          </>
        ) : loading && total === 0 ? (
          'Zorgverleners laden…'
        ) : (
          <>
            Alle <strong className="text-ink-900">{nl.format(total)}</strong> {noun}
          </>
        )}
      </div>

      {narrowed && (
        <div className="flex min-w-0 flex-wrap items-center gap-1.5">
          <span className="text-xs font-semibold text-ink-800/70">Je zoekt op:</span>
          {chips.map((c) => (
            <button
              key={c.key}
              type="button"
              onClick={c.onRemove}
              title={`${c.group} ${c.label} verwijderen`}
              aria-label={`Filter ${c.group} ${c.label} verwijderen`}
              className="inline-flex max-w-full items-center gap-1 rounded-full border border-brand-300 bg-white py-0.5 pl-2.5 pr-1.5 text-[12px] font-medium text-ink-900 transition-colors hover:border-brand-600 hover:bg-brand-50"
            >
              <span className="text-ink-900/50">{c.group}:</span>
              <span className="truncate">{c.label}</span>
              <XIcon size={12} className="shrink-0 text-brand-700" />
            </button>
          ))}
          <button
            type="button"
            onClick={onReset}
            className="ml-1 text-[12px] font-semibold text-brand-700 underline-offset-2 hover:text-ink-900 hover:underline"
          >
            Alles wissen
          </button>
        </div>
      )}
    </div>
  );
}

function FilterGroup({ label, children }: { label: string; children: ReactNode }): JSX.Element {
  return (
    <div>
      <div className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-ink-800/60">
        {label}
      </div>
      {children}
    </div>
  );
}

function RadioRow({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onChange}
      className={`flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-left text-sm transition-colors ${
        checked
          ? 'bg-brand-50 font-medium text-ink-900'
          : 'text-ink-900/70 hover:bg-brand-50/60 hover:text-ink-900'
      }`}
    >
      <span
        className={`flex h-4 w-4 items-center justify-center rounded-full border-[1.5px] ${
          checked ? 'border-brand-700 bg-brand-700' : 'border-brand-300'
        }`}
      >
        {checked && <span className="h-1.5 w-1.5 rounded-full bg-white" />}
      </span>
      {label}
    </button>
  );
}

function Chip({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1 rounded-full border px-3 py-1 text-[12px] font-medium transition-colors ${
        active
          ? 'border-brand-700 bg-brand-700 text-white shadow-sm'
          : 'border-brand-200 bg-white text-ink-800/80 hover:border-brand-400 hover:text-ink-900'
      }`}
    >
      {active && <CheckIcon size={11} strokeWidth={3} />}
      {label}
    </button>
  );
}

function ViewToggle({
  active,
  onClick,
  icon,
  label,
}: {
  active: boolean;
  onClick: () => void;
  icon: ReactNode;
  label: string;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium transition-colors ${
        active ? 'bg-white text-ink-900 shadow-sm' : 'text-ink-900/60 hover:text-ink-900'
      }`}
      title={label}
      aria-label={label}
    >
      {icon} <span className="hidden sm:inline">{label}</span>
    </button>
  );
}

function LetterButton({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex h-7 min-w-[1.75rem] shrink-0 items-center justify-center rounded-md px-1.5 text-[12px] font-semibold transition-colors ${
        active
          ? 'bg-brand-700 text-white shadow-sm'
          : 'text-ink-800/70 hover:bg-brand-50 hover:text-ink-900'
      }`}
    >
      {label}
    </button>
  );
}

function ProviderRow({
  provider,
  onEdit,
  showSource = false,
}: {
  provider: Provider;
  onEdit?: () => void;
  /** Admin-only: show the "LVVP" provenance badge. POH's don't see it. */
  showSource?: boolean;
}): JSX.Element {
  return (
    <div className="flex items-center gap-3 px-4 py-3 transition-colors hover:bg-brand-50/40">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <h3 className="truncate text-sm font-semibold text-ink-900">{provider.name}</h3>
          {showSource && provider.source === 'lvvp' && (
            <span className="inline-flex shrink-0 items-center rounded-full bg-petrol-50 px-1.5 py-0.5 text-[10px] font-medium text-petrol-700">
              LVVP
            </span>
          )}
        </div>
        <div className="mt-0.5 flex items-center gap-1.5 text-[12px] text-ink-900/60">
          <span className="text-brand-700/70">{typeLabel(provider.type)}</span>
          <span aria-hidden="true">·</span>
          <span className="truncate">
            {[provider.location.city, provider.location.province].filter(Boolean).join(' · ')}
          </span>
          {locationTypeLabel(provider.location.locationType) && (
            <span className="shrink-0 rounded-full bg-brand-50 px-1.5 py-0.5 text-[10.5px] font-medium text-brand-700">
              {locationTypeLabel(provider.location.locationType)}
            </span>
          )}
          {provider.additionalLocations && provider.additionalLocations.length > 0 && (
            <span className="shrink-0 rounded-full bg-ink-900/5 px-1.5 py-0.5 text-[10.5px] font-medium text-ink-900/70">
              +{provider.additionalLocations.length} locaties
            </span>
          )}
          {provider.intakeStopped &&
            (() => {
              const ins = provider.intakeStoppedInsurers ?? [];
              const partial = ins.length > 0;
              return (
                <span
                  className={`inline-flex shrink-0 items-center gap-1 rounded-full px-1.5 py-0.5 text-[10.5px] font-semibold ${
                    partial ? 'bg-amber-50 text-amber-800' : 'bg-red-50 text-red-700'
                  }`}
                  title={
                    partial
                      ? `Geen nieuwe cliënten voor: ${ins.join(', ')}`
                      : provider.intakeStoppedReason || 'Neemt tijdelijk geen nieuwe cliënten aan'
                  }
                >
                  <BanIcon size={10} />{' '}
                  {partial
                    ? ins.length <= 2
                      ? `Stop: ${ins.join(', ')}`
                      : `Stop: ${ins.length} verzekeraars`
                    : 'Aanmeldstop'}
                </span>
              );
            })()}
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-1.5">
        {provider.contactInfo.phone && (
          <a
            href={`tel:${provider.contactInfo.phone}`}
            className="inline-flex items-center justify-center rounded-md border border-brand-200 p-1.5 text-ink-800 transition-colors hover:bg-brand-50"
            title="Bellen"
          >
            <PhoneIcon size={13} />
          </a>
        )}
        {provider.contactInfo.website && (
          <a
            href={provider.contactInfo.website}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center justify-center rounded-md border border-brand-200 p-1.5 text-ink-800 transition-colors hover:bg-brand-50"
            title="Website"
          >
            <GlobeIcon size={13} />
          </a>
        )}
        {onEdit && (
          <button
            type="button"
            onClick={onEdit}
            className="inline-flex items-center justify-center rounded-md border border-brand-200 p-1.5 text-ink-800 transition-colors hover:bg-brand-50 hover:text-brand-700"
            title="Aanbieder bewerken (alleen voor SUPPOHRT-admins)"
          >
            <PencilIcon size={13} />
          </button>
        )}
      </div>
    </div>
  );
}

function ZeroResults({
  onReset,
  hasFilters,
}: {
  onReset: () => void;
  hasFilters: boolean;
}): JSX.Element {
  return (
    <div className="rounded-2xl border border-dashed border-brand-200 bg-white p-10 text-center">
      <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-brand-50 text-brand-600">
        <InboxIcon size={24} />
      </div>
      <h3 className="mt-4 font-display text-lg font-bold text-ink-900">
        Geen zorgverleners gevonden
      </h3>
      <p className="mx-auto mt-1 max-w-md text-sm text-ink-900/60">
        {hasFilters
          ? 'De huidige filtercombinatie levert geen resultaten op. Verwijder een filter of meld dit als aanvulling.'
          : 'Nog geen aanbieders beschikbaar. Neem contact op met de beheerder.'}
      </p>
      {hasFilters && (
        <div className="mt-6 flex items-center justify-center gap-2">
          <button
            type="button"
            onClick={onReset}
            className="inline-flex items-center gap-1.5 rounded-lg border border-brand-200 px-3.5 py-2 text-sm font-medium text-ink-800 transition-colors hover:bg-brand-50"
          >
            Wis filters
          </button>
        </div>
      )}
    </div>
  );
}

function ProviderCard({
  provider,
  onEdit,
  showSource = false,
}: {
  provider: Provider;
  /** When defined, the card surfaces an admin-only "Bewerk" affordance. */
  onEdit?: () => void;
  /** Admin-only: show the "Bron: LVVP" provenance chip. POH's don't see it. */
  showSource?: boolean;
}): JSX.Element {
  return (
    <div className="group relative rounded-xl border border-brand-100 bg-white p-4 transition-all hover:border-brand-300 hover:shadow-card">
      {onEdit && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onEdit();
          }}
          className="absolute right-3 top-3 z-10 inline-flex items-center gap-1 rounded-md border border-brand-200 bg-white/95 px-2 py-1 text-[11px] font-medium text-ink-800 opacity-0 shadow-sm transition-all hover:bg-brand-50 hover:text-brand-700 focus:opacity-100 group-hover:opacity-100"
          title="Aanbieder bewerken (alleen voor SUPPOHRT-admins)"
        >
          <PencilIcon size={11} /> Bewerk
        </button>
      )}
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[11px] font-medium uppercase tracking-wide text-brand-700/70">
            {typeLabel(provider.type)}
          </div>
          <h3 className="mt-0.5 text-[15px] font-semibold leading-tight text-ink-900">
            {provider.name}
          </h3>
        </div>
        {/* Wachttijd-status pill verwijderd 2026-05-08 — Linda houdt
            wachttijden niet meer bij; pill leidde af zonder informatie. */}
      </div>

      <div className="mt-2 flex items-start gap-1.5 text-[12.5px] text-ink-900/70">
        <MapPinIcon
          size={13}
          aria-hidden="true"
          className="mt-0.5 h-[13px] w-[13px] flex-shrink-0 text-brand-700/60"
        />
        <span>
          {[provider.location.city, provider.location.province].filter(Boolean).join(' · ')}
        </span>
        {locationTypeLabel(provider.location.locationType) && (
          <span className="rounded-full bg-brand-50 px-1.5 py-0.5 text-[10.5px] font-medium text-brand-700">
            {locationTypeLabel(provider.location.locationType)}
          </span>
        )}
        {provider.additionalLocations && provider.additionalLocations.length > 0 && (
          <span className="shrink-0 rounded-full bg-ink-900/5 px-1.5 py-0.5 text-[10.5px] font-medium text-ink-900/70">
            +{provider.additionalLocations.length} locaties
          </span>
        )}
      </div>

      <div className="mt-2">
        <IntakeControl provider={provider} />
      </div>

      <CareLevelPills provider={provider} className="mt-3" />

      {provider.specialisations.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {provider.specialisations.slice(0, 6).map((s) => (
            <span
              key={s}
              className="rounded-full border border-brand-200 bg-white px-2.5 py-0.5 text-[11px] font-medium text-ink-800/80"
            >
              {specialisationLabel(s)}
            </span>
          ))}
        </div>
      )}

      {provider.targetAudience.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2 text-[11.5px] text-ink-900/60">
          {provider.targetAudience.map((a) => (
            <span key={a} className="inline-flex items-center">
              • {audienceLabel(a)}
            </span>
          ))}
        </div>
      )}

      {/* Wachttijd-strook verwijderd 2026-05-08 — niet meer onderhouden. */}

      {(provider.contactInfo.phone || provider.contactInfo.website) && (
        <div className="mt-3 flex items-center gap-2 border-t border-brand-50 pt-3">
          {provider.contactInfo.phone && (
            <a
              href={`tel:${provider.contactInfo.phone}`}
              className="inline-flex flex-1 items-center justify-center gap-2 rounded-lg border border-brand-200 px-3 py-2 text-xs font-medium text-ink-800 transition-colors hover:bg-brand-50"
            >
              <PhoneIcon size={12} /> Bellen
            </a>
          )}
          {provider.contactInfo.website && (
            <a
              href={provider.contactInfo.website}
              target="_blank"
              rel="noreferrer"
              className="inline-flex flex-1 items-center justify-center gap-2 rounded-lg bg-brand-800 px-3 py-2 text-xs font-medium text-white shadow-sm transition-colors hover:bg-brand-900"
            >
              <GlobeIcon size={12} /> Website
            </a>
          )}
        </div>
      )}

      {/* Meta row: verified date (+ provenance, admin-only) links, report-this-
          card knop rechts. Bron is bewust stille tekst i.p.v. een pill: het is
          voetnoot-metadata en petrol-pills zijn gereserveerd voor de Ring-
          vertrouwenschips (DESIGN.md). Alleen source='lvvp' toont herkomst;
          suppohrt_manual records blijven zonder. */}
      <div className="mt-3 flex flex-wrap items-center justify-between gap-x-2 gap-y-1.5 border-t border-brand-50 pt-2.5 text-[10.5px] text-ink-900/45">
        <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
          <span className="whitespace-nowrap">
            Geverifieerd {formatDate(provider.lastVerifiedAt)}
          </span>
          {showSource &&
            provider.source === 'lvvp' &&
            (provider.sourceUrl ? (
              <a
                href={provider.sourceUrl}
                target="_blank"
                rel="noreferrer"
                className="whitespace-nowrap underline decoration-ink-900/25 underline-offset-2 hover:text-petrol-700"
                title="Bekijk het bronrecord op LVVP"
              >
                · Bron: LVVP
              </a>
            ) : (
              <span className="whitespace-nowrap">· Bron: LVVP</span>
            ))}
        </div>
        <WijzigingsverzoekLink
          className="-my-0.5 ml-auto shrink-0 rounded-md border border-brand-200 px-2 py-1.5 hover:border-brand-300 hover:bg-brand-50"
          context={{
            surface: 'sociale-kaart',
            providerId: provider.id,
            providerName: provider.name,
            city: provider.location.city ?? null,
          }}
        />
      </div>
    </div>
  );
}

/**
 * Aanmeldstop-strook — weergave + self-service bewerken, open voor elke
 * ingelogde gebruiker (net als de wachttijd vroeger, ADR 0012). Toont de
 * praktijk die tijdelijk geen nieuwe cliënten aanneemt (vaak door het
 * omzetplafond van de verzekeraar) met een rode badge, zodat je in één oogopslag
 * ziet wie vol zit zonder de website te checken of te bellen. Slaat direct op via
 * PATCH en werkt optimistisch bij zonder de lijst te herladen.
 */
/**
 * Aanvragen-tab (admin) — de wachtrij met door POH's voorgestelde praktijken
 * (status 'pending'). Goedkeuren activeert de praktijk; afwijzen verwijdert het
 * voorstel. Beide via de suggestion-endpoints die serverside op status==='pending'
 * guarden.
 */
function PendingReview({
  providers,
  onDecision,
  onClose,
}: {
  providers: Provider[];
  onDecision: () => void;
  onClose: () => void;
}): JSX.Element {
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function decide(id: string, action: 'approve' | 'reject'): Promise<void> {
    setBusyId(id);
    setError(null);
    try {
      await apiPost(`/api/v1/providers/suggestions/${encodeURIComponent(id)}/${action}`);
      onDecision();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Actie mislukt');
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="font-display text-lg font-bold text-ink-900">Aanvragen</h2>
          <p className="text-sm text-ink-900/60">
            Door POH&apos;s voorgestelde praktijken. Na goedkeuring verschijnen ze in de Sociale
            Kaart.
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="shrink-0 rounded-md border border-brand-200 px-3 py-1.5 text-xs font-medium text-ink-800 hover:bg-brand-50"
        >
          Terug naar kaart
        </button>
      </div>

      {error && <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}

      {providers.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-brand-200 bg-white p-10 text-center">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-brand-50 text-brand-600">
            <InboxIcon size={24} />
          </div>
          <h3 className="mt-4 font-display text-lg font-bold text-ink-900">
            Geen openstaande aanvragen
          </h3>
          <p className="mx-auto mt-1 max-w-md text-sm text-ink-900/60">
            Nieuwe praktijkvoorstellen van POH&apos;s verschijnen hier ter beoordeling.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {providers.map((p) => (
            <div key={p.id} className="rounded-xl border border-brand-100 bg-white p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-[11px] font-medium uppercase tracking-wide text-brand-700/70">
                    {typeLabel(p.type)}
                  </div>
                  <h3 className="mt-0.5 text-[15px] font-semibold leading-tight text-ink-900">
                    {p.name}
                  </h3>
                  <div className="mt-1 flex items-center gap-1.5 text-[12.5px] text-ink-900/70">
                    <MapPinIcon size={13} className="shrink-0 text-brand-700/60" />
                    <span>
                      {[p.location.address, p.location.city, p.location.province]
                        .filter(Boolean)
                        .join(' · ')}
                    </span>
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <button
                    type="button"
                    disabled={busyId === p.id}
                    onClick={() => void decide(p.id, 'approve')}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-brand-700 disabled:opacity-50"
                  >
                    <CheckIcon size={13} /> Goedkeuren
                  </button>
                  <button
                    type="button"
                    disabled={busyId === p.id}
                    onClick={() => void decide(p.id, 'reject')}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-brand-200 px-3 py-1.5 text-xs font-medium text-ink-800 transition-colors hover:bg-red-50 hover:text-red-700 disabled:opacity-50"
                  >
                    <XIcon size={13} /> Afwijzen
                  </button>
                </div>
              </div>

              {p.specialisations.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {p.specialisations.slice(0, 8).map((s) => (
                    <span
                      key={s}
                      className="rounded-full border border-brand-200 bg-white px-2.5 py-0.5 text-[11px] font-medium text-ink-800/80"
                    >
                      {specialisationLabel(s)}
                    </span>
                  ))}
                </div>
              )}

              {(p.contactInfo.phone || p.contactInfo.website) && (
                <div className="mt-3 flex flex-wrap items-center gap-3 text-[12px] text-ink-900/60">
                  {p.contactInfo.phone && <span>{p.contactInfo.phone}</span>}
                  {p.contactInfo.website && (
                    <a
                      href={p.contactInfo.website}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 text-petrol-600 hover:underline"
                    >
                      <GlobeIcon size={12} /> Website
                    </a>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function audienceLabel(a: string): string {
  return AUDIENCES.find((x) => x.value === a)?.label ?? a;
}

/**
 * Wat de POH moet weten als er géén adres staat. Zonder dit label ziet een
 * online-aanbieder er op de kaart uit als een praktijk waar je iemand naartoe
 * kunt sturen — en dat is precies de verwarring die locationType wegneemt.
 * `fysiek` en pre-migratie records (locationType afwezig) krijgen niets: daar
 * spreekt het adres voor zich.
 */
export function locationTypeLabel(t: Provider['location']['locationType']): string | null {
  switch (t) {
    case 'online':
      return 'Alleen online';
    case 'meerdere_locaties':
      return 'Meerdere locaties';
    case 'onbekend':
      return 'Adres onbekend';
    default:
      return null;
  }
}

export function typeLabel(type: Provider['type']): string {
  switch (type) {
    case 'ggz_instelling':
      return 'GGZ-instelling';
    case 'vrijgevestigd':
      return 'Vrijgevestigd';
    case 'groepspraktijk':
      return 'Groepspraktijk';
    case 'e_health':
      return 'E-health';
    default:
      return 'Overig';
  }
}

function formatDate(iso: string): string {
  try {
    return new Intl.DateTimeFormat('nl-NL', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}
